import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Plus } from "lucide-react";

import { getIncomingRequest, getOutgoingRequest } from "src/api/inventory";
import { useAuth } from "src/providers/AuthProvider";
import useHasPermissions from "src/hooks/useHasPermissions";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import CustomModal from "src/components/CustomModal";
import ApproveRequest from "src/components/modals/ApproveRequest";
import RequestDetail from "src/components/modals/RequestDetail";
import RequestProducts from "src/components/modals/RequestProducts";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { BranchSelect, DataTable, PageHead, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue } from "src/lib/utils";
import type { StatusValue } from "src/components/common/StatusBadge";

interface RequestRow {
	uuid: string;
	from_name?: string;
	to_name?: string;
	status?: string;
	requested_qty?: number;
	approved_qty?: number;
	created_at?: string;
}

type RequestType = "incoming" | "outgoing";

const requestStatusBadge = (status?: string): StatusValue => {
	if (status === "approved") return "approved";
	if (status === "declined") return "declined";
	return "pending";
};

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "PPP");
};

const tabTriggerClass =
	"!tw-bg-white !tw-text-text-soft data-[state=active]:!tw-bg-navy data-[state=active]:!tw-text-white tw-rounded-lg tw-border tw-border-border tw-text-[13px]";

const InventoryRequest = () => {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const inventoryLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";
	const entity = searchParams.get("entity") ?? user?.store_or_warehouse_uuid ?? "";

	const setEntity = (value: string) => {
		const next = new URLSearchParams(searchParams);
		if (value === "0") next.delete("entity");
		else next.set("entity", value);
		setSearchParams(next);
	};

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={`${inventoryLabel} Requests`}
				subtitle="Stock moving between your branches and warehouses."
				actions={
					<>
						<Link
							to="/inventory"
							className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-2 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
						>
							<ArrowLeft className="tw-h-4 tw-w-4" />
							Back to {inventoryLabel}
						</Link>
						<CustomModal
							title="Request Products"
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Plus className="tw-h-4 tw-w-4" />
									Request Products
								</Button>
							}
						>
							<RequestProducts />
						</CustomModal>
					</>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<PermissionWrapper permission="view_all_store_requests">
					<BranchSelect value={entity} onChange={setEntity} allLabel="All stores" />
				</PermissionWrapper>
			</div>

			<Tabs defaultValue="incoming">
				<TabsList className="tw-mb-4 !tw-gap-1 !tw-bg-transparent !tw-p-0">
					<TabsTrigger value="incoming" className={tabTriggerClass}>
						Incoming Requests
					</TabsTrigger>
					<TabsTrigger value="outgoing" className={tabTriggerClass}>
						Outgoing Requests
					</TabsTrigger>
				</TabsList>
				<TabsContent value="incoming">
					<RequestTable type="incoming" />
				</TabsContent>
				<TabsContent value="outgoing">
					<RequestTable type="outgoing" />
				</TabsContent>
			</Tabs>
		</div>
	);
};

const RequestTable = ({ type }: { type: RequestType }) => {
	const { user } = useAuth();
	const canApprove = useHasPermissions("approve_inventory_request");
	const [searchParams, setSearchParams] = useSearchParams();

	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);
	const entity = searchParams.get("entity") ?? user?.store_or_warehouse_uuid;

	const { data, isLoading } = useQuery({
		queryKey: ["requests", type, entity, page, limit],
		queryFn: () => {
			const params = { entity, page, limit: limit === "All" ? 9999 : limit };
			return type === "incoming" ? getIncomingRequest(params) : getOutgoingRequest(params);
		},
	});

	const list = data?.data;
	const requests = list?.data ?? [];

	const columns: Column<RequestRow>[] = [
		{
			key: "party",
			header: type === "incoming" ? "From" : "To",
			primary: true,
			cell: (row) => <span className="tw-capitalize">{(type === "incoming" ? row.from_name : row.to_name) ?? "—"}</span>,
		},
		{ key: "status", header: "Status", cell: (row) => <StatusBadge status={requestStatusBadge(row.status)} label={row.status} /> },
		{ key: "requested", header: "Requested Qty", cell: (row) => row.requested_qty ?? "—" },
		{ key: "approved", header: "Approved Qty", cell: (row) => (row.status === "approved" ? row.approved_qty ?? "—" : "—") },
		{ key: "date", header: "Date", cell: (row) => formatDate(row.created_at) },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[150px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1">
					<CustomModal
						title="Request Detail"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
							>
								Detail
							</button>
						}
					>
						<RequestDetail requestUUID={row.uuid} />
					</CustomModal>

					{canApprove && type === "outgoing" && row.status === "pending" && (
						<CustomModal
							title="Approve Request"
							trigger={
								<button
									type="button"
									className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
								>
									Approve
								</button>
							}
						>
							<ApproveRequest requestUUID={row.uuid} />
						</CustomModal>
					)}
				</div>
			),
		},
	];

	return (
		<DataTable
			data={requests}
			columns={columns}
			rowKey={(row) => row.uuid}
			loading={isLoading}
			totalRows={list?.total ?? requests.length}
			page={page}
			onPageChange={(next) => {
				const params = new URLSearchParams(searchParams);
				params.set("page", String(next));
				setSearchParams(params);
			}}
			rowsPerPage={limit}
			onRowsPerPageChange={(next) => {
				const params = new URLSearchParams(searchParams);
				params.set("limit", String(next));
				params.set("page", "1");
				setSearchParams(params);
			}}
			emptyTitle={type === "incoming" ? "No incoming requests" : "No outgoing requests"}
			emptyDescription="Stock requests between your branches will appear here."
		/>
	);
};

export default withPermissionWrapper(InventoryRequest, "view_store_requests");
